/* workspace/WorkspaceRuntimeContext — Workspace Runtime Foundation (Phase 5).
   Single GET /workspaces/:wsId/runtime call is the authoritative source for
   workspace metadata, member context, branding, capabilities, and limits.
   5-minute module-level cache per wsId — survives provider remounts.
   Refreshes automatically when nx-session-changed fires for the active workspace.
   Renders WorkspaceUnavailablePage transparently when status is SUSPENDED/ARCHIVED. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn } from '../shared/ui';
import { getWorkspaceRuntime } from '../api/workspace-runtime.api.js';

/* Module-level cache — survives provider remounts within the same browser session */
const RUNTIME_CACHE = new Map();
const TTL_MS = 5 * 60 * 1000;

function isFresh(entry) {
  return entry && Date.now() - entry.fetchedAt < TTL_MS;
}

export function invalidateWorkspaceRuntime(wsId) {
  if (wsId) {
    RUNTIME_CACHE.delete(wsId);
  } else {
    RUNTIME_CACHE.clear();
  }
}

/* ── WorkspaceUnavailablePage ────────────────────────────────────────────── */
/* Rendered in-place when workspace.status is SUSPENDED or ARCHIVED.
   The provider still mounts children inside the context so consumers can
   observe the status, but this interstitial replaces the visible content. */
function WorkspaceUnavailablePage({ status }) {
  const navigate = useNavigate();
  const isArchived = status === 'ARCHIVED';
  return (
    <div className="nx-rbac-denied">
      <span className="nx-rbac-denied-icon">
        <NexusIcon name={isArchived ? 'Archive' : 'PauseCircle'} size={28} />
      </span>
      <h2 className="nx-rbac-denied-title">
        Workspace {isArchived ? 'Archived' : 'Suspended'}
      </h2>
      <p className="nx-rbac-denied-desc">
        {isArchived
          ? 'This workspace has been archived and is no longer accessible.'
          : 'This workspace has been suspended. Contact your organization admin for more information.'}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="primary" icon="LayoutGrid" onClick={() => navigate('/workspaces')}>
          Switch Workspace
        </Btn>
      </div>
    </div>
  );
}

/* ── Provider ────────────────────────────────────────────────────────────── */
const WsRuntimeCtx = createContext(null);

export function WorkspaceRuntimeProvider({ workspaceId, children }) {
  const [runtime, setRuntime] = useState(
    () => RUNTIME_CACHE.get(workspaceId)?.data ?? null,
  );
  const [loading, setLoading] = useState(!isFresh(RUNTIME_CACHE.get(workspaceId)));
  const [error, setError] = useState(null);
  const inflightRef = useRef(false);
  const fetchingWsRef = useRef(null);
  const mountedRef = useRef(true);

  /* Reset local state immediately when workspaceId prop changes. */
  useEffect(() => {
    inflightRef.current = false;
    const cached = RUNTIME_CACHE.get(workspaceId);
    if (isFresh(cached)) {
      setRuntime(cached.data);
      setLoading(false);
      setError(null);
    } else {
      setRuntime(null);
      setLoading(true);
      setError(null);
    }
  }, [workspaceId]);

  const fetchRuntime = useCallback(
    async (bust = false) => {
      if (!workspaceId) return;
      if (!bust && inflightRef.current) return;
      const cached = RUNTIME_CACHE.get(workspaceId);
      if (!bust && isFresh(cached)) {
        setRuntime(cached.data);
        setLoading(false);
        return;
      }
      inflightRef.current = true;
      fetchingWsRef.current = workspaceId;
      setLoading(true);
      setError(null);
      try {
        const data = await getWorkspaceRuntime(workspaceId);
        RUNTIME_CACHE.set(workspaceId, { data, fetchedAt: Date.now() });
        if (mountedRef.current && fetchingWsRef.current === workspaceId) {
          setRuntime(data);
          setLoading(false);
        }
      } catch (err) {
        if (mountedRef.current && fetchingWsRef.current === workspaceId) {
          setError(err?.message ?? 'Failed to load workspace runtime');
          setLoading(false);
        }
      } finally {
        if (fetchingWsRef.current === workspaceId) {
          inflightRef.current = false;
        }
      }
    },
    [workspaceId],
  );

  /* Initial fetch on mount and on workspaceId change */
  useEffect(() => {
    mountedRef.current = true;
    fetchRuntime(false);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchRuntime]);

  /* Refresh when the session is updated for the same active workspace.
     A workspace switch changes the workspaceId prop (triggering the useEffect above);
     this listener handles permission changes while the workspace is unchanged. */
  useEffect(() => {
    const onSessionChanged = (e) => {
      const nextWsId = e.detail?.user?.activeWorkspaceId;
      if (nextWsId === workspaceId) {
        RUNTIME_CACHE.delete(workspaceId);
        fetchRuntime(true);
      }
    };
    window.addEventListener('nx-session-changed', onSessionChanged);
    return () => window.removeEventListener('nx-session-changed', onSessionChanged);
  }, [workspaceId, fetchRuntime]);

  /* Shared invalidation handler — used by both module and membership change events.
     Invalidates the cache and re-fetches when the event targets this workspace. */
  const handleRuntimeInvalidation = useCallback((e) => {
    const changedWsId = e?.detail?.workspaceId;
    if (!changedWsId || changedWsId === workspaceId) {
      RUNTIME_CACHE.delete(workspaceId);
      fetchRuntime(true);
    }
  }, [workspaceId, fetchRuntime]);

  /* H-03: Immediate runtime refresh when modules change.
     Modules.jsx dispatches 'nx-modules-changed' after a successful save so the
     sidebar, routes, and drawers reflect the new module list without a page reload. */
  useEffect(() => {
    window.addEventListener('nx-modules-changed', handleRuntimeInvalidation);
    return () => window.removeEventListener('nx-modules-changed', handleRuntimeInvalidation);
  }, [handleRuntimeInvalidation]);

  /* R4-07: Refresh runtime when membership changes — dispatched by Membership.jsx
     after a successful add/remove so the member context stays current. */
  useEffect(() => {
    window.addEventListener('nx-members-changed', handleRuntimeInvalidation);
    return () => window.removeEventListener('nx-members-changed', handleRuntimeInvalidation);
  }, [handleRuntimeInvalidation]);

  const refreshRuntime = useCallback(() => {
    RUNTIME_CACHE.delete(workspaceId);
    fetchRuntime(true);
  }, [workspaceId, fetchRuntime]);

  /* Optimistically patch branding so UI updates without a full refetch. */
  const patchBranding = useCallback(
    (patch) => {
      setRuntime((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, branding: { ...prev.branding, ...patch } };
        const cached = RUNTIME_CACHE.get(workspaceId);
        if (cached) RUNTIME_CACHE.set(workspaceId, { data: updated, fetchedAt: cached.fetchedAt });
        return updated;
      });
    },
    [workspaceId],
  );

  const ctx = useMemo(
    () => ({
      runtime,
      loading,
      error,
      refreshRuntime,
      patchBranding,
      invalidateRuntime: (id) => invalidateWorkspaceRuntime(id ?? workspaceId),
    }),
    [runtime, loading, error, refreshRuntime, patchBranding, workspaceId],
  );

  /* Status gate: render an interstitial for SUSPENDED / ARCHIVED workspaces.
     Children are still mounted inside the context so hooks remain usable. */
  const wsStatus = runtime?.workspace?.status;
  const isUnavailable =
    !loading && !error && wsStatus && wsStatus !== 'ACTIVE' && wsStatus !== 'ONBOARDING';

  return (
    <WsRuntimeCtx.Provider value={ctx}>
      {isUnavailable ? <WorkspaceUnavailablePage status={wsStatus} /> : children}
    </WsRuntimeCtx.Provider>
  );
}

/* ── Hooks ───────────────────────────────────────────────────────────────── */

export function useWorkspaceRuntime() {
  const ctx = useContext(WsRuntimeCtx);
  if (!ctx) throw new Error('useWorkspaceRuntime must be used inside WorkspaceRuntimeProvider');
  return ctx;
}

export function useWorkspace() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.workspace ?? null;
}

export function useWorkspaceStatus() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.workspace?.status ?? null;
}

export function useWorkspaceCapabilities() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.capabilities ?? [];
}

export function useWorkspaceRole() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.member?.wsRole ?? null;
}

export function useWorkspaceModules() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.workspace?.modules ?? [];
}

export function useWorkspaceBranding() {
  const { runtime, patchBranding } = useWorkspaceRuntime();
  return { branding: runtime?.branding ?? null, patchBranding };
}

export function useWorkspaceLimits() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.limits ?? {};
}

export function useWorkspaceMember() {
  const { runtime } = useWorkspaceRuntime();
  return runtime?.member ?? null;
}
